import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';
import { EffectFade, Navigation } from 'swiper/modules';
import './styles.scss';
import SwiperClass from 'swiper/types/swiper-class';
import SuggestionsSliderScrollbar from './SuggestionsSliderScrollbar';
import SuggestionsSingleSlide from './SuggestionsSingleSlide';
import { getConversation_conversations_data_messages } from '../../../__generated__/getConversation';
import { useState } from 'react';
import useConversation from '../hooks/useConversation';
import useAnalytics from '../hooks/useAnalytics';
import { TRACK_EVENT } from 'utils/analytics';
import { useMutation } from '@apollo/client';
import { UPDATE_STYLE_MESSAGE } from 'graph_ql/chat/mutation.gql';
import { MessageVisibleEnum } from '../../../__generated__/globalTypes';
import { useChatContext } from '../hooks/ChatContext';

SwiperCore.use([EffectFade, Navigation]);

interface ISuggestionsSliderProps {
    messages: getConversation_conversations_data_messages[];
    startIndex: number;
    shouldScrollToSlider: boolean;
}

const SuggestionsSlider = ({ messages, startIndex, shouldScrollToSlider }: ISuggestionsSliderProps) => {
    const { conversation } = useConversation();
    const { messageContainerRef, scrollTopRef, messages: allMessages, sendMessage, handleScroll } = useChatContext();
    const handleAnalytics = useAnalytics();
    const [displayedStyles, setDisplayedStyles] = useState<string[]>([]);
    const [openedInfoMessage, setOpenedInfoMessage] = useState<string | null>(null);

    const [updateStyleMessage] = useMutation(UPDATE_STYLE_MESSAGE, {
        context: {
            allowAnonymousAuth: true,
        },
        onError(error) {
            console.error('Create Failed: ', error);
        },
    });

    const trackStyleChatView = (swiper: SwiperClass) => {
        const activeSlide = swiper.slides[swiper.activeIndex];
        const activeSlideIndex = activeSlide?.dataset.index ? parseInt(activeSlide.dataset.index, 10) : null;
        if (
            activeSlideIndex !== null &&
            messages[activeSlideIndex].taffiId &&
            !displayedStyles.includes(messages[activeSlideIndex].taffiId as string)
        ) {
            const message = messages[activeSlideIndex];
            const element =
                message.elements && Array.isArray(message.elements) && message.elements.length
                    ? message.elements[0]
                    : null;

            handleAnalytics(
                TRACK_EVENT.STYLE_CHAT_PRODUCT_DISPLAYED,
                {
                    ...message,
                    conversationId: conversation?.id,
                },
                element,
            );
            if (displayedStyles.length && message.visible === MessageVisibleEnum.invisible) {
                handleAnalytics(TRACK_EVENT.STYLE_CHAT_SHOW_MORE_STYLES_CLICKED, {
                    conversationId: conversation?.id,
                });
                // send request to make the current message visible
                updateStyleMessage({
                    variables: {
                        input: {
                            taffiId: message.taffiId,
                            visible: MessageVisibleEnum.visible,
                        },
                    },
                });
            }
            if (message.taffiId) {
                setDisplayedStyles((prev) => [...prev, message.taffiId as string]);
            }
        }
    };

    const onInit = async (swiper: SwiperClass) => {
        trackStyleChatView(swiper);
        try {
            if (shouldScrollToSlider && messageContainerRef.current) {
                // get swiper height
                const swiperHeight = swiper.height;
                messageContainerRef.current.scrollTop = messageContainerRef?.current?.scrollHeight - swiperHeight;
                scrollTopRef.current = messageContainerRef?.current?.scrollTop || 0;
            }
            // handle the case where the conversation is not complete and the last message in allMessages array is not a feedback message
            if (allMessages.length > 1 && allMessages[allMessages.length - 1].type !== 'feedback') {
                // loop through all messages and send show more message if the message does not have an answer
                const activeSlide = swiper.slides[swiper.activeIndex];
                const activeSlideIndex = activeSlide?.dataset.index ? parseInt(activeSlide.dataset.index, 10) : null;
                if (activeSlideIndex !== null && !messages[activeSlideIndex].answer) {
                    setTimeout(() => {
                        sendMessage({
                            conversationId: messages[activeSlideIndex].conversationId,
                            taffiId: messages[activeSlideIndex].taffiId as any,
                            scope: messages[activeSlideIndex].scope,
                            inAppValue: 'show more',
                            inAppMessage: '',
                        });
                    }, 1000);
                }
            }
        } catch (error) {
            console.error('Error setting scrollTop in showMoreElement', error);
        }
    };

    return (
        <div className={`amira-suggestions-container ${messages.length === 1 ? 'only-one-recommendation' : ''}`}>
            <Swiper
                loop={messages.length > 1}
                autoplay={false}
                navigation={{
                    nextEl: `#taffi-swiper-button-next-${startIndex}`,
                    prevEl: `#taffi-swiper-button-prev-${startIndex}`,
                }}
                slidesPerView={messages.length > 1 ? 1.24 : 1}
                spaceBetween={12}
                onInit={onInit}
                onSlideChangeTransitionStart={(swiper: SwiperClass) => {
                    setOpenedInfoMessage(null);
                    setTimeout(() => {
                        handleScroll();
                    }, 500);
                }}
                onSlideChangeTransitionEnd={(swiper: SwiperClass) => {
                    trackStyleChatView(swiper);
                }}
            >
                {messages.map((message: getConversation_conversations_data_messages, index: number) => (
                    <SwiperSlide key={index} data-index={index}>
                        <SuggestionsSingleSlide
                            message={message}
                            messageIndex={index}
                            openedInfoMessage={openedInfoMessage}
                            setOpenedInfoMessage={setOpenedInfoMessage}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
            {messages.length > 1 && <SuggestionsSliderScrollbar elementSuffix={startIndex} />}
        </div>
    );
};

export default SuggestionsSlider;
